import { FC, memo } from 'react';
import classNames from 'classnames';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import {
  NOTIFICATION_DEFAULT_DELAY,
  NotificationColors,
  useNotificationContext,
} from 'application/providers/NotificationProvider';
import { DOWNLOAD_APP_PAGE_PATH } from 'application/pages/DownloadAppPage';
import AnalyticsService, { StreamBindFlowEventNameAMP } from 'application/services/AnalyticsService';

import IconCopy from '@zvuk-b2b/react-uikit/ui/icons/Copy';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';

import { TEXTS } from './texts';
import { useOnboardingContext } from '../../useOnboardingContext';
import { useOnboardingActionContext } from '../../useOnboardingActionContext';
import { FLOWS_FOR_NOT_SHOW_STOP_ONBOARDING_WARNING } from '../../consts';

import styles from './DownloadMobileModal.module.scss';

const QR_CODE_PATH = '/assets/img/app/downloadAppQr.png';

const DownloadMobileModal: FC<ModalProps[ModalTypes.MOBILE_PLAYER_DOWNLOAD]> = ({ openModal, replaceModal, close }) => {
  const { flowType } = useOnboardingContext();
  const { clearCurrentPoint } = useOnboardingActionContext();

  const notification = useNotificationContext();

  const siteUrl = window.location.origin;
  const downloadAppFullPath = `${siteUrl}${DOWNLOAD_APP_PAGE_PATH}`;

  const onClickCopy = () => {
    navigator.clipboard.writeText(downloadAppFullPath);

    notification.showNotification({
      type: NotificationColors.INFO,
      children: TEXTS.LINK_WAS_COPIED,
      delay: NOTIFICATION_DEFAULT_DELAY,
    });
  };

  const onDownloadClick = () => {
    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_MOBILE_DOWNLOAD_CLICKED);
    replaceModal(ModalTypes.MOBILE_PLAYER_BIND, {});
  };

  const handleCancel = () => {
    if (flowType && FLOWS_FOR_NOT_SHOW_STOP_ONBOARDING_WARNING.has(flowType)) {
      close();
      clearCurrentPoint();
      return;
    }

    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
  };

  return (
    <ModalScreen
      titlePlacement="left"
      onClose={handleCancel}
      className={styles.modalContainer}
      contentClassName={styles.modalContent}
      title={TEXTS.MODAL_TITLE}
      containerActionButtonsClassName={styles.actionButtonsContainer}
      actionButtons={[
        <Button
          key="button_mobile_player_modal_download_success"
          elementId="button_mobile_player_modal_download_success"
          type={ButtonType.PRIMARY}
          onClick={onDownloadClick}>
          {TEXTS.SUCCESS_BUTTON}
        </Button>,
        <Button
          key="button_mobile_player_modal_download_cancel"
          elementId="button_mobile_player_modal_download_cancel"
          type={ButtonType.SECONDARY}
          onClick={() => {
            replaceModal(ModalTypes.SELECT_PLAYER_MODAL, {});
          }}>
          {TEXTS.CANCEL_BUTTON}
        </Button>,
      ]}>
      <div className={styles.description}>{TEXTS.DESCRIPTION}</div>

      <div className={styles.qrContainer}>
        <img
          src={QR_CODE_PATH}
          alt=""
          loading="lazy"
          className={styles.qr}
        />

        <Button
          type={ButtonType.PLAIN}
          className={classNames(styles.copyButton, styles.button)}
          onClick={onClickCopy}
          elementId="button_mobile_player_modal_copy_link">
          <div className={styles.copyButtonIcon}>
            <IconCopy />
          </div>
          <span>{TEXTS.DOWNLOAD_LINK}</span>
        </Button>
      </div>
    </ModalScreen>
  );
};

export default memo(DownloadMobileModal);
